import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import "./index.scss"

import { cn, EnumsOf } from "@/lib/utils"
import RoundedButton from "@/components/rounded-button"
import { Icon } from "@/icons/icon"

/**
 * @example
 * ```jsx
 *  <TruckTabs defaultValue="account">
 *       <TruckTabsList>
 *           <TruckTabsTrigger value="account" indicator="success">
 *                    Account
 *           </TruckTabsTrigger>
 *           <TruckTabsTrigger value="password" indicator="error">
 *                 Password
 *            </TruckTabsTrigger>
 *       </TruckTabsList>
 *
 *       <TruckTabsContent value="account">
 *           Make changes to your account here.
 *       </TruckTabsContent>
 *       <TruckTabsContent value="password">
 *           Change your password here.
 *       </TruckTabsContent>
 *  </TruckTabs>
 * ```
 */
function TruckTabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("truck-tabs flex flex-col gap-2", className)}
      {...props}
    />
  )
}

export type TruckTabsListProps = React.ComponentProps<
  typeof TabsPrimitive.List
> & {
  canAdd?: boolean
  onClickAdd?: () => void
}

function TruckTabsList({
  className,
  canAdd = true,
  onClickAdd,
  ...props
}: TruckTabsListProps) {
  return (
    <div className="flex items-center gap-4 overflow-auto">
      <TabsPrimitive.List
        data-slot="tabs-list"
        className={cn(
          `
          flex gap-4 w-full overflow-auto
        `,
          className
        )}
        {...props}
      />

      {canAdd && (
        <RoundedButton icon={<Icon name="Plus" />} onClick={onClickAdd}>
          Truck
        </RoundedButton>
      )}
    </div>
  )
}

export const TruckTabIndicator = {
  Error: "Error",
  Success: "Success",
  Warning: "Warning",
  None: "None",
} as const
export type ProductTabIndicator = EnumsOf<typeof TruckTabIndicator>

type TruckTabsTriggerProps = React.ComponentProps<
  typeof TabsPrimitive.Trigger
> & {
  indicator?: ProductTabIndicator
}

function TruckTabsTrigger({
  className,
  indicator,
  ...props
}: TruckTabsTriggerProps) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        `
          text-label-text data-[state=active]:text-button-black focus-visible:border-ring 
          focus-visible:ring-ring/50 focus-visible:outline-ring
          active-indicator relative inline-flex flex-1 items-center 
          justify-center gap-1.5 px-2 py-2 text-sm
          font-medium transition-[color,box-shadow] first:border-l-0
          last:border-r-0 hover:cursor-pointer focus-visible:ring-[3px]
          focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50
          [&_svg]:pointer-events-none [&_svg]:shrink-0
          [&_svg:not([class*='size-'])]:size-4 h-13 w-max text-nowrap
        `,
        indicator && `indicator ${indicator}`,
        className
      )}
      {...props}
    />
  )
}

function TruckTabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export { TruckTabs, TruckTabsList, TruckTabsTrigger, TruckTabsContent }
